import { v4 } from 'uuid';
import invoiceLabels from '../../config/InvoiceLabels.json';
import moment from 'moment';
import { Inference, InferenceFile, Algorithm, TestResult, Model, TableColumn, ClassificationResult } from '../../types';
import { Label } from '../../types/label';
import { ResultField } from '../../types/result';
import { User } from '../../types/users';

export const getElementOffset = (el: HTMLElement) => {
  var rect = el.getBoundingClientRect(),
    scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
    scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  return { top: rect.top + scrollTop, left: rect.left + scrollLeft };
};

export const getLabelValue = (labelName: string) => {
  const invoiceLabel = invoiceLabels.find(invoiceLabel => invoiceLabel.name === labelName);
  return (invoiceLabel && invoiceLabel.translation) || labelName;
};

// FILE, ANNOTATION, RESULT

export const groupAnnotationsByPage = (annotations: Label[]) => {
  if (!annotations) {
    return {};
  }

  return annotations.filter(Boolean).reduce((res, annotation) => {
    const { page } = annotation;
    if (!page) return res;
    res[page] = res[page] || [];
    res[page].push(annotation);

    return res;
  }, {} as { [page: number]: Label[] });
};

export const getResultInfo = (label: Partial<Label>) => {
  if (label.manuallyApproved) {
    return 'label-manually-validated';
  } else if (label.source === 'manual') {
    return 'label-manually-added';
  } else if (label.reliable) {
    return label.exists ? 'label-exists' : 'label-not-present';
  } else {
    return 'label-unlikely-correct';
  }
};

export const processTestResult = (file: any) => {
  let file_: TestResult = {
    ...file,
    resultFields: file.fields.map((field: any) => ({
      id: field.id,
      name: field.name,
      correct: field.correct,
      exists: field.result.positions.length ? true : false,
      reliable: field.reliable,
      positions: field.result.positions[0] ? [field.result.positions[0]] : [],
      hasCorrection: field.annotation
        ? {
            correct: field.correct,
            correction: {
              exists: true,
              position: field.annotation.positions[0]?.location || null,
              ...field.annotation.positions[0],
            },
            name: field.name,
          }
        : null,
    })),
  };
  return file_;
};

export const processInferenceResult = (inferenceFile: InferenceFile) => {
  const m: Model = inferenceFile.model;

  if ((m.algorithm.multiflow && inferenceFile.run?.id && inferenceFile.inferenceResult) || m.algorithm.id === 1) {
    const results = inferenceFile.inferenceResult?.result;

    inferenceFile.inferenceResult.result = {
      properties: results.properties,
      result: null,
      name: '',
      type: results.type,
      fields: results.fields.map(it => ({
        ...it,
        ...it.positions[0],
      })),
      tables: results.tables.map(table => {
        let tableContent: { [key: string]: TableColumn }[] = [];
        const tableLabels: Label[] = []; //edited
        if (results.tables && results.tables.length) {
          tableContent = table.content.map((row: { [key: string]: TableColumn }) => {
            let result: any = {};
            Object.keys(row).forEach(function(key) {
              result[key] = {
                ...row[key],
                id: generateUUID(),
                isTable: true,
                reliable: true,
                exists: true,
                name: key,
                ...row[key].positions[0],
              };
              tableLabels.push(result[key]);
            });
            return result;
          });
        }

        return { ...table, content: tableContent, labels: tableLabels };
      }),
    };
  }

  return inferenceFile;
};

export const createFileInfo = (file: { id: string; fileName: string }) => {
  const fileInfo = {
    fileId: file.id,
    fileName: file.fileName,
    fields: [],
  };
  return fileInfo;
};

export const getClassificationResult = (inference: Inference | InferenceFile) => {
  return (
    (inference &&
      inference.inferenceResult &&
      inference.inferenceResult.result &&
      inference.inferenceResult.result.type &&
      (inference.inferenceResult.result.type as ClassificationResult).name) ||
    (inference && inference.annotations && inference.annotations.type && inference.annotations.type.name)
  );
};

//INBOX RESULT FIELDS

export const getIconForInboxResultField = (resultField: ResultField) => {
  if (!resultField.exists) {
    if (resultField.hasCorrection) {
      if (resultField.hasCorrection.correct) {
        return 'check';
      } else {
        return 'exclamation-circle';
      }
    } else {
      return 'minus';
    }
  } else if (!resultField.hasCorrection) {
    return resultField.reliable ? 'check' : 'exclamation-circle';
  } else {
    return resultField.hasCorrection.correct ? 'check' : 'exclamation-circle';
  }
};

export const getColorForInboxResultField = (resultField: ResultField) => {
  if (!resultField.hasCorrection) {
    return '#8c8c8c';
  } else if (!resultField.hasCorrection.correct) {
    return '#fa541c';
  } else {
    return '#52c41a';
  }
};

export const getBackgroundColorForInboxResultField = (resultField: ResultField) => {
  if (!resultField.hasCorrection) {
    return '#e8e8e8';
  } else if (!resultField.hasCorrection.correct) {
    return '#ffd8bf';
  } else {
    return '#d9f7be';
  }
};

export const getLabelColorForInboxResultField = (resultField: ResultField, type: string) => {
  if (type === 'correction') {
    return '#52c41a';
  } else if (resultField.hasCorrection) {
    return resultField.hasCorrection.correct ? '#52c41a' : '#fa541c';
  } else {
    return resultField.reliable ? '#52c41a' : '#fa541c';
  }
};

export const groupResultFieldsByPage = (resultFields: ResultField[]) => {
  if (!resultFields) {
    return {};
  }

  return resultFields
    .filter(Boolean)
    .filter(f => (f.exists && f.positions[0]) || (!f.exists && f.hasCorrection))
    .reduce((res, resultField) => {
      let page;
      if (resultField.exists) {
        page = resultField.positions[0].page;
      } else {
        page = resultField.hasCorrection?.correction?.page;
      }

      if (!page) return res;
      res[page] = res[page] || [];
      res[page].push(resultField);

      return res;
    }, {} as { [page: number]: ResultField[] });
};

//ALGORITHM TYPES

export const isDocumentClassification = (algorithm: null | Algorithm) => {
  return algorithm && algorithm.type === 'classification';
};

export const isMultiflow = (algorithm: null | Algorithm) => {
  return algorithm && algorithm.type === 'multiflow';
};

export const isKeywordDetection = (algorithm: null | Algorithm) => {
  return algorithm && algorithm.type === 'keyword';
};

//RANDOM

export const slugify = (value: string) => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

export const formatTime = (timeString: string) => {
  return moment(timeString).format('lll');
};

export const generateUUID = () => {
  return v4();
};

export const userIsAdmin = (currentUser: User | null) => {
  //return true;
  return currentUser && currentUser.role && currentUser.role === 'ADMIN';
};
